import { useMemo, useState } from "react";
import { MoreHorizontal, RefreshCw, Search } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import AdminLayout from "@/components/layout/AdminLayout";
import { useUsers, type PlatformUser } from "@/hooks/useUsers";
import { supabase } from "@/integrations/supabase/client";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";

type ActionKind = "impersonate" | "suspend" | "reactivate" | "deactivate";

interface ActionTarget {
  kind: ActionKind;
  user: PlatformUser;
}

const RoleBadge = ({ role }: { role: string | null | undefined }) => {
  const r = (role ?? "").toLowerCase();
  if (r === "superuser") {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-500/15 text-purple-300 border border-purple-500/30">
        Superuser
      </span>
    );
  }
  if (r === "admin") {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-500/15 text-blue-300 border border-blue-500/30">
        Admin
      </span>
    );
  }
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-slate-700/40 text-slate-300 border border-slate-600/40">
      Member
    </span>
  );
};

const StatusBadge = ({ status }: { status: string | null | undefined }) => {
  const s = (status ?? "").toLowerCase();
  if (s === "active") {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-emerald-500/15 text-emerald-400 border border-emerald-500/30">
        Active
      </span>
    );
  }
  if (s === "suspended") {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-500/15 text-amber-400 border border-amber-500/30">
        Suspended
      </span>
    );
  }
  if (s === "pending") {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-500/15 text-blue-300 border border-blue-500/30">
        Pending
      </span>
    );
  }
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-slate-700/40 text-slate-400 border border-slate-600/40">
      Deactivated
    </span>
  );
};

const fullName = (u: PlatformUser): string => {
  const name = `${u.first_name ?? ""} ${u.last_name ?? ""}`.trim();
  return name || u.email || "Unnamed user";
};

const isProfileComplete = (u: PlatformUser): boolean => {
  return Boolean((u.first_name ?? "").trim() && (u.last_name ?? "").trim());
};

export default function Users() {
  const queryClient = useQueryClient();
  const { users, isLoading, error, refetch } = useUsers();

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [companyFilter, setCompanyFilter] = useState<string>("all");

  const [target, setTarget] = useState<ActionTarget | null>(null);
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const companies = useMemo(() => {
    const set = new Set<string>();
    for (const u of users) {
      const name = (u.company_name ?? "").trim();
      if (name) set.add(name);
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [users]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return users.filter((u) => {
      if (statusFilter !== "all" && (u.status ?? "").toLowerCase() !== statusFilter) return false;
      if (companyFilter !== "all" && (u.company_name ?? "") !== companyFilter) return false;
      if (q) {
        const hay = `${fullName(u)} ${u.email ?? ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [users, search, statusFilter, companyFilter]);

  const closeDialog = () => {
    if (submitting) return;
    setTarget(null);
    setReason("");
  };

  const openAction = (kind: ActionKind, user: PlatformUser) => {
    setTarget({ kind, user });
    setReason("");
  };

  const handleConfirm = async () => {
    if (!target) return;
    const { kind, user } = target;
    const trimmed = reason.trim();

    if ((kind === "suspend" || kind === "deactivate") && !trimmed) {
      toast.error("A reason is required");
      return;
    }

    setSubmitting(true);
    try {
      if (kind === "impersonate") {
        const { error: rpcErr } = await supabase.rpc("admin_impersonate_user", {
          p_user_id: user.id,
        });
        if (rpcErr) throw rpcErr;
        window.open("https://app.doyencegroup.com", "_blank");
        return;
      }

      if (kind === "suspend") {
        if (!user.company_id) throw new Error("User has no company");
        const { error: rpcErr } = await supabase.rpc("suspend_member", {
          p_user_id: user.id,
          p_company_id: user.company_id,
          p_reason: trimmed,
          p_suspended_until: undefined,
        });
        if (rpcErr) throw rpcErr;
        toast.success("User suspended");
      } else if (kind === "reactivate") {
        if (!user.company_id) throw new Error("User has no company");
        const { error: rpcErr } = await supabase.rpc("reactivate_company_member", {
          p_user_id: user.id,
          p_company_id: user.company_id,
        });
        if (rpcErr) throw rpcErr;
        toast.success("User reactivated");
      } else if (kind === "deactivate") {
        if (!user.company_id) throw new Error("User has no company");
        const { error: rpcErr } = await supabase.rpc("deactivate_user_from_company", {
          p_user_id: user.id,
          p_company_id: user.company_id,
          p_reason: trimmed,
        });
        if (rpcErr) throw rpcErr;
        toast.success("User deactivated");
      }

      queryClient.invalidateQueries({ queryKey: ["platform-users"] });
      setTarget(null);
      setReason("");
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Action failed";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const dialogConfig = useMemo(() => {
    if (!target) return null;
    const name = fullName(target.user);
    switch (target.kind) {
      case "impersonate":
        return {
          title: `Impersonate ${name}?`,
          body: `You will be redirected to the main app as ${name}. An impersonation session will be recorded in the audit log. The session expires after 5 minutes of inactivity.`,
          confirmLabel: "Impersonate",
          confirmClass: "bg-amber-500 hover:bg-amber-600 text-slate-950",
          requireReason: false,
          reasonLabel: "",
        };
      case "suspend":
        return {
          title: `Suspend ${name}?`,
          body: `This will immediately block ${name}'s access. They will see a suspended account screen.`,
          confirmLabel: "Suspend",
          confirmClass: "bg-amber-500 hover:bg-amber-600 text-slate-950",
          requireReason: true,
          reasonLabel: "Reason for suspension",
        };
      case "reactivate":
        return {
          title: `Reactivate ${name}?`,
          body: `This will restore ${name}'s access.`,
          confirmLabel: "Reactivate",
          confirmClass: "bg-emerald-600 hover:bg-emerald-700 text-white",
          requireReason: false,
          reasonLabel: "",
        };
      case "deactivate":
        return {
          title: `Deactivate ${name}?`,
          body: `This will permanently deactivate ${name}'s account. They will need to be manually reactivated by a company admin or superuser.`,
          confirmLabel: "Deactivate",
          confirmClass: "bg-red-600 hover:bg-red-700 text-white",
          requireReason: true,
          reasonLabel: "Reason for deactivation",
        };
    }
  }, [target]);

  return (
    <AdminLayout title="Platform Users">
      <div className="space-y-4">
        {/* Toolbar */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[200px] max-w-sm">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-500" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name or email…"
              className="pl-8 h-9 bg-slate-900 border-slate-700 text-slate-100 placeholder:text-slate-500"
            />
          </div>

          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="h-9 w-[160px] bg-slate-900 border-slate-700 text-slate-100">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent className="bg-slate-900 border-slate-700 text-slate-100">
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="suspended">Suspended</SelectItem>
              <SelectItem value="deactivated">Deactivated</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
            </SelectContent>
          </Select>

          <Select value={companyFilter} onValueChange={setCompanyFilter}>
            <SelectTrigger className="h-9 w-[200px] bg-slate-900 border-slate-700 text-slate-100">
              <SelectValue placeholder="Company" />
            </SelectTrigger>
            <SelectContent className="bg-slate-900 border-slate-700 text-slate-100">
              <SelectItem value="all">All companies</SelectItem>
              {companies.map((c) => (
                <SelectItem key={c} value={c}>
                  {c}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <div className="ml-auto flex items-center gap-3">
            <span className="text-xs text-slate-400">
              Showing {filtered.length} of {users.length} users
            </span>
            <Button
              size="icon"
              variant="outline"
              onClick={() => refetch()}
              className="h-9 w-9 border-slate-700 bg-slate-800 hover:bg-slate-700 text-slate-200"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${isLoading ? "animate-spin" : ""}`} />
            </Button>
          </div>
        </div>

        {/* Table */}
        <div className="bg-slate-900 border border-slate-800 rounded-md overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-800 text-slate-400 uppercase text-xs">
              <tr>
                <th className="text-left font-medium px-3 py-2">Name</th>
                <th className="text-left font-medium px-3 py-2">Email</th>
                <th className="text-left font-medium px-3 py-2">Company</th>
                <th className="text-left font-medium px-3 py-2">Role</th>
                <th className="text-left font-medium px-3 py-2">Status</th>
                <th className="text-right font-medium px-3 py-2 w-12">Actions</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <>
                  {Array.from({ length: 8 }).map((_, i) => (
                    <tr key={i} className="border-b border-slate-800">
                      <td className="px-3 py-2.5"><Skeleton className="h-4 w-32 bg-slate-800" /></td>
                      <td className="px-3 py-2.5"><Skeleton className="h-4 w-48 bg-slate-800" /></td>
                      <td className="px-3 py-2.5"><Skeleton className="h-4 w-28 bg-slate-800" /></td>
                      <td className="px-3 py-2.5"><Skeleton className="h-4 w-16 bg-slate-800" /></td>
                      <td className="px-3 py-2.5"><Skeleton className="h-4 w-20 bg-slate-800" /></td>
                      <td className="px-3 py-2.5"><Skeleton className="h-4 w-6 ml-auto bg-slate-800" /></td>
                    </tr>
                  ))}
                </>
              )}

              {!isLoading && error && (
                <tr>
                  <td colSpan={6} className="px-3 py-12 text-center">
                    <p className="text-sm text-red-400 mb-3">{error}</p>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => refetch()}
                      className="border-slate-700 bg-slate-800 hover:bg-slate-700 text-slate-200"
                    >
                      <RefreshCw className="h-3.5 w-3.5 mr-2" />
                      Retry
                    </Button>
                  </td>
                </tr>
              )}

              {!isLoading && !error && filtered.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-3 py-12 text-center text-sm text-slate-400">
                    No users match your filters.
                  </td>
                </tr>
              )}

              {!isLoading && !error && filtered.map((u) => {
                const role = (u.admin_role ?? "").toLowerCase();
                const status = (u.status ?? "").toLowerCase();
                const isSuperuser = role === "superuser";
                const canImpersonate = status === "active" && !isSuperuser;
                const canSuspend = status === "active";
                const canReactivate = status === "suspended";
                const canDeactivate = status === "active" || status === "suspended";
                const hasAnyAction = canImpersonate || canSuspend || canReactivate || canDeactivate;

                return (
                  <tr key={u.id} className="border-b border-slate-800 hover:bg-slate-800/50">
                    <td className="px-3 py-2.5">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-white">{fullName(u)}</span>
                        {!isProfileComplete(u) && (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-slate-700/40 text-slate-400 border border-slate-600/40">
                            Incomplete
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-2.5 text-slate-400">{u.email || "—"}</td>
                    <td className="px-3 py-2.5">
                      {u.company_name ? (
                        <span className="text-slate-300">{u.company_name}</span>
                      ) : (
                        <span className="text-slate-500">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2.5"><RoleBadge role={u.admin_role} /></td>
                    <td className="px-3 py-2.5"><StatusBadge status={u.status} /></td>
                    <td className="px-3 py-2.5 text-right">
                      {hasAnyAction && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-7 w-7 text-slate-400 hover:text-white hover:bg-slate-700"
                            >
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent
                            align="end"
                            className="bg-slate-900 border-slate-700 text-slate-100"
                          >
                            {canImpersonate && (
                              <DropdownMenuItem
                                onClick={() => openAction("impersonate", u)}
                                className="focus:bg-slate-800 focus:text-white"
                              >
                                Impersonate
                              </DropdownMenuItem>
                            )}
                            {canSuspend && (
                              <DropdownMenuItem
                                onClick={() => openAction("suspend", u)}
                                className="text-amber-400 focus:bg-slate-800 focus:text-amber-300"
                              >
                                Suspend User
                              </DropdownMenuItem>
                            )}
                            {canReactivate && (
                              <DropdownMenuItem
                                onClick={() => openAction("reactivate", u)}
                                className="text-emerald-400 focus:bg-slate-800 focus:text-emerald-300"
                              >
                                Reactivate User
                              </DropdownMenuItem>
                            )}
                            {canDeactivate && (
                              <DropdownMenuItem
                                onClick={() => openAction("deactivate", u)}
                                className="text-red-400 focus:bg-slate-800 focus:text-red-300"
                              >
                                Deactivate User
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Confirmation dialog */}
      <Dialog open={!!target} onOpenChange={(o) => !o && closeDialog()}>
        <DialogContent className="bg-slate-900 border-slate-800 text-slate-100">
          {dialogConfig && (
            <>
              <DialogHeader>
                <DialogTitle className="text-white">{dialogConfig.title}</DialogTitle>
                <DialogDescription className="text-slate-400">
                  {dialogConfig.body}
                </DialogDescription>
              </DialogHeader>
              {dialogConfig.requireReason && (
                <div className="space-y-2">
                  <Label htmlFor="reason" className="text-slate-300">
                    {dialogConfig.reasonLabel} <span className="text-red-400">*</span>
                  </Label>
                  <Input
                    id="reason"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    className="bg-slate-950 border-slate-700 text-slate-100"
                    autoFocus
                  />
                </div>
              )}
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={closeDialog}
                  disabled={submitting}
                  className="border-slate-700 bg-slate-800 hover:bg-slate-700 text-slate-200"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleConfirm}
                  disabled={submitting || (dialogConfig.requireReason && !reason.trim())}
                  className={dialogConfig.confirmClass}
                >
                  {submitting ? "Working…" : dialogConfig.confirmLabel}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
